import { Injectable, inject, signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse, Settings, SettingsDto } from '../../../core/models/api.models';
import { filterAuthorizedKnowledgeSources, KnowledgeSource, normalizeKnowledgeSources } from '../../../core/models/knowledge-source';
import { AuthenticatedHttpService } from '../../../core/http/authenticated-http.service';
import { ThemeService } from '../../../core/services/theme.service';

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly http = inject(AuthenticatedHttpService);
  private readonly theme = inject(ThemeService);
  readonly value = signal<Settings | null>(null);
  readonly availableKnowledgeSources = signal<KnowledgeSource[]>(['public']);
  readonly selectedKnowledgeSources = signal<KnowledgeSource[]>(['public']);
  readonly error = signal<string | null>(null);
  load(): Observable<ApiResponse<Settings>> {
    return this.http.get<ApiResponse<SettingsDto>>(`${environment.apiUrl}/Settings`).pipe(
      map(result => ({ ...result, value: this.normalize(result.value) })),
      tap(result => this.apply(result.value)),
    );
  }
  save(settings: Settings): Observable<ApiResponse<Settings>> {
    const { knowledgeSources: _readOnlyPermissions, ...payload } = settings;
    return this.http.post<ApiResponse<SettingsDto>>(`${environment.apiUrl}/Settings`, payload).pipe(
      map(result => ({ ...result, value: this.normalize(result.value, this.availableKnowledgeSources()) })),
      tap(result => this.apply(result.value)),
    );
  }

  selectKnowledgeSources(sources: readonly KnowledgeSource[]): void {
    const authorized = filterAuthorizedKnowledgeSources(sources, this.availableKnowledgeSources());
    if (authorized.length) this.selectedKnowledgeSources.set(authorized);
  }

  private normalize(settings: SettingsDto, fallback?: readonly KnowledgeSource[]): Settings {
    const rawSources = settings.knowledgeSources == null && fallback ? fallback : settings.knowledgeSources;
    return { ...settings, knowledgeSources: normalizeKnowledgeSources(rawSources) };
  }

  private apply(settings: Settings): void {
    const previousAvailable = this.availableKnowledgeSources();
    const previousSelected = this.selectedKnowledgeSources();
    const firstLoad = this.value() === null;
    this.value.set(settings);
    this.availableKnowledgeSources.set(settings.knowledgeSources);
    if (firstLoad) {
      this.selectedKnowledgeSources.set([...settings.knowledgeSources]);
    } else if (previousAvailable.some(source => !settings.knowledgeSources.includes(source))) {
      const authorized = filterAuthorizedKnowledgeSources(previousSelected, settings.knowledgeSources);
      this.selectedKnowledgeSources.set(authorized.length ? authorized : ['public']);
    }
    this.theme.set(settings.darkMode ? 'dark' : 'light');
  }
}
